import { useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { Upload } from "lucide-react";

export default function SalesImport() {
  const [imports, setImports] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);
  const [matchSel, setMatchSel] = useState({});
  const [newAccounts, setNewAccounts] = useState([]);

  const load = () => {
    api.get("/admin/sales/imports").then((r) => setImports(r.data));
    api.get("/admin/sales/unmatched").then((r) => setUnmatched(r.data));
    api.get("/projects").then((r) => setProjects(r.data.filter((p) => p.status !== "draft")));
  };
  useEffect(load, []);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sales/import", fd);
      if (data.report_type === "kdp_orders") {
        toast.success(`Sales report imported: ${data.matched_rows} new rows, ${data.updated_rows || 0} updated to latest, ${data.duplicates_skipped} unchanged skipped, ${data.projects_created} books & ${data.authors_created} authors created`);
        setNewAccounts(data.authors_created_accounts || []);
      } else {
        toast.success(`Imported ${data.total_rows} rows — ${data.matched_rows} matched, ${data.unmatched_rows} unmatched`);
      }
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const match = async (rowId) => {
    const projectId = matchSel[rowId];
    if (!projectId) return toast.error("Choose a project first");
    try {
      await api.put(`/admin/sales/rows/${rowId}/match`, { project_id: projectId });
      toast.success("Row matched");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="sales-import-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-neutral-900 font-heading">Sales Imports</h1>
        <p className="text-neutral-500 mt-1">Upload Amazon sales reports (CSV/XLSX). Rows are matched to projects via ASIN.</p>
      </div>

      <label className="border-2 border-dashed border-neutral-300 rounded-lg p-10 flex flex-col items-center justify-center cursor-pointer hover:border-neutral-900 transition-colors bg-white mb-8">
        <Upload size={28} className="text-neutral-400 mb-3" />
        <span className="text-sm font-medium text-neutral-700">{busy ? "Importing..." : "Click to upload a sales report (.csv / .xlsx)"}</span>
        <span className="text-xs text-neutral-400 mt-1">Full Amazon Orders workbooks are auto-detected — authors & books are matched or created automatically</span>
        <input data-testid="import-sales-file-input" type="file" accept=".csv,.xlsx,.xls" className="hidden" disabled={busy}
          onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
      </label>

      {newAccounts.length > 0 && (
        <div className="bg-white border border-blue-200 rounded-lg overflow-hidden mb-8" data-testid="new-accounts-panel">
          <h3 className="font-semibold font-heading p-6 pb-2 text-blue-800">New author accounts created ({newAccounts.length})</h3>
          <p className="px-6 text-sm text-neutral-500 pb-2">Send each author their claim link so they can set their own email & password — or share the temporary credentials directly. Shown only once.</p>
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left">
            <thead><tr className="bg-neutral-50">
              {["Author", "Email (login)", "Temp password", "Claim link"].map((h) => (
                <th key={h} className="px-6 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>))}
            </tr></thead>
            <tbody className="divide-y divide-neutral-100">
              {newAccounts.map((a) => (
                <tr key={a.email} data-testid={`new-account-${a.email}`}>
                  <td className="px-6 py-3 font-medium text-neutral-900">{a.name}</td>
                  <td className="px-6 py-3 font-mono text-xs">{a.email}</td>
                  <td className="px-6 py-3 font-mono text-xs font-semibold">{a.temp_password}</td>
                  <td className="px-6 py-3">
                    <button data-testid={`copy-claim-${a.email}`}
                      onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/claim?token=${a.claim_token}`); toast.success("Claim link copied"); }}
                      className="px-3 py-1.5 border border-neutral-300 rounded-md text-xs font-medium text-neutral-700 hover:bg-neutral-50">Copy link</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {unmatched.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-lg overflow-hidden mb-8">
          <h3 className="font-semibold font-heading p-6 pb-2 text-amber-800">Unmatched rows ({unmatched.length})</h3>
          <p className="px-6 text-sm text-neutral-500 pb-2">These ASINs didn't match any project. Match them manually below.</p>
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left">
            <thead><tr className="bg-neutral-50">
              {["ASIN", "Title", "Marketplace", "Units", "Royalty", "Match to project", ""].map((h, i) => (
                <th key={i} className="px-4 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>))}
            </tr></thead>
            <tbody className="divide-y divide-neutral-100">
              {unmatched.map((r) => (
                <tr key={r.id} data-testid={`unmatched-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{r.asin}</td>
                  <td className="px-4 py-3">{r.title || "—"}</td>
                  <td className="px-4 py-3">{r.marketplace}</td>
                  <td className="px-4 py-3">{r.units}</td>
                  <td className="px-4 py-3">${r.royalty.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <select data-testid={`match-select-${r.id}`} value={matchSel[r.id] || ""}
                      onChange={(e) => setMatchSel((m) => ({ ...m, [r.id]: e.target.value }))}
                      className="border border-neutral-300 rounded-md px-2 py-1.5 text-sm bg-white w-48">
                      <option value="">Choose project...</option>
                      {projects.map((p) => <option key={p.id} value={p.id}>{p.title} — {p.author_name}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <button data-testid={`match-button-${r.id}`} onClick={() => match(r.id)}
                      className="px-3 py-1.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-xs font-medium">Match</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <h3 className="font-semibold font-heading p-6 pb-2">Import audit log</h3>
        {imports.length === 0 ? <p className="p-6 pt-2 text-sm text-neutral-400">No imports yet.</p> : (
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left">
            <thead><tr className="bg-neutral-50">
              {["Date", "File", "Imported by", "Rows", "Matched", "Unmatched"].map((h) => (
                <th key={h} className="px-6 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>))}
            </tr></thead>
            <tbody className="divide-y divide-neutral-100">
              {imports.map((im) => (
                <tr key={im.id} data-testid={`import-row-${im.id}`}>
                  <td className="px-6 py-3">{new Date(im.created_at).toLocaleString()}</td>
                  <td className="px-6 py-3 font-medium">{im.filename}</td>
                  <td className="px-6 py-3">{im.imported_by_name}</td>
                  <td className="px-6 py-3">{im.total_rows}</td>
                  <td className="px-6 py-3 text-emerald-700 font-medium">{im.matched_rows}</td>
                  <td className="px-6 py-3 text-amber-700 font-medium">{im.unmatched_rows}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
